import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { CreditProcessStatus, CreditStatus } from 'src/app/@core/constants';
import { ColumnTable } from 'src/app/models/column-table';
import { CreditRequest } from 'src/app/models/credit-request';
import { CreditRequestRequirement } from 'src/app/models/credit-request-requirements';
import { OnbaseDoc } from 'src/app/models/onbase-doc';
import { Paging } from 'src/app/models/page';
import { ResponseModel } from 'src/app/models/response-model';
import { OnbaseProcedureResponseModel } from 'src/app/models/response/onbaseProcedureResponseModel';
import { ExceptionSolutionService } from 'src/app/services/exception-solution.service';
import { OnbaseService } from 'src/app/services/onbase.service';
import Swal from 'sweetalert2';
import { PosCreditRequestRequirementDetailModalService } from './../../../../@core/services/pos-credit-request-requirement-detail-modal.service';
import { ImportRequirementDocumentRequest } from './../../../../models/request/import-requirement-document-request';
import { PosTimeAgoComponent } from './../../../../shared/pos-time-ago/pos-time-ago.component';

@Component({
  selector: 'app-credit-request-detail',
  templateUrl: './credit-request-detail.component.html',
  styleUrls: ['./credit-request-detail.component.sass'],
})
export class CreditRequestDetailComponent
  implements OnInit, OnChanges, OnDestroy {
  @ViewChild('timer') timer!: PosTimeAgoComponent;
  @ViewChild('requirementsTimer') requirementsTimer!: PosTimeAgoComponent;

  @Input() creditRequestNumber = '';
  @Input() showButtons = true;

  @Output() getRequest = new EventEmitter<CreditRequest>();

  creditPlans: Array<OnbaseProcedureResponseModel> = new Array();
  onbaseDocs: Array<OnbaseDoc> = new Array();

  creditRequest: CreditRequest = new CreditRequest();
  creditRequestInitialDate = new Date();
  creditRequestEndDate = new Date();

  requirements: Paging<Array<CreditRequestRequirement>> = new Paging([]);
  firstRequirementDate = new Date();
  lastRequirementCompletedDate = new Date();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly datePipe: DatePipe,
    private readonly router: Router,
    private readonly onbaseService: OnbaseService,
    private readonly requirementDetailModalService: PosCreditRequestRequirementDetailModalService,
    private readonly exceptionSolutionService: ExceptionSolutionService
  ) {
    this.creditRequest = new CreditRequest();
    this.creditRequestNumber =
      this.route.snapshot.params.creditRequestNumber ||
      this.creditRequestNumber;
  }

  ngOnDestroy(): void {
    if (this.timer) {
      this.timer.stop();
    }

    if (this.requirementsTimer) {
      this.requirementsTimer.stop();
    }
  }

  async ngOnInit(): Promise<void> {
    this.route.paramMap.subscribe(async (params) => {

      this.creditRequestNumber = params.get('creditRequestNumber') || this.creditRequestNumber;

      this.creditRequest = (
        await this.readCreditRequestAndTransformDate(this.creditRequestNumber)
      ).response;

      if (!this.creditRequest) {
        this.creditRequest = new CreditRequest();
      }

      if (this.creditRequest.processingStatus !== 'DRAFT') {
        await this.setAndConfigureDataForTimers();
      }

      this.creditPlans = (
        await this.onbaseService.readCreditPlans().toPromise()
      ).response.content;

      this.onbaseDocs = (
        await this.onbaseService.readOnBaseDocs().toPromise()
      ).response;

      this.getRequest.emit(this.creditRequest);
    });
  }

  private async setAndConfigureDataForTimers() {
    if (this.timer) {
      this.timer.stop();
    }

    if (this.requirementsTimer) {
      this.requirementsTimer.stop();
    }

    const dataForTimer = (
      await this.onbaseService
        .getTimerSellerAndCustomer(this.creditRequest.code, `CUSTOMERTIME`)
        .toPromise()
    ).response;

    this.creditRequestInitialDate = dataForTimer.initialDate;
    this.creditRequestEndDate = dataForTimer.endDate;

    this.timer.initialControlTimer(dataForTimer);

    const dataForRequirementTimer = (
      await this.onbaseService
        .getTimerSellerAndCustomer(this.creditRequest.code, `SALESTIME`)
        .toPromise()
    ).response;

    this.firstRequirementDate = dataForRequirementTimer.initialDate;
    this.lastRequirementCompletedDate = dataForRequirementTimer.endDate;

    this.requirementsTimer.initialControlTimer(dataForRequirementTimer);
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    this.creditRequest = (
      await this.readCreditRequestAndTransformDate(this.creditRequestNumber)
    ).response;
    if (!this.creditRequest) {
      this.creditRequest = new CreditRequest();
    }
    this.getRequest.emit(this.creditRequest);
  }

  private readCreditRequestAndTransformDate(creditRequestNumber: string) {
    return this.onbaseService
      .readOnbaseRequestByCode(creditRequestNumber)
      .pipe(
        map((onbaseRequestResponse) => {
          this.parseRequirements(onbaseRequestResponse);
          return this.transformDate(onbaseRequestResponse);
        })
      )
      .toPromise();
  }

  private parseRequirements(
    onbaseRequestResponse: ResponseModel<CreditRequest>
  ) {
    const onbaseRequest = onbaseRequestResponse.response;
    this.requirements = new Paging(
      Object.keys(onbaseRequest.requirements).map(
        (key: string, index: number) => {
          onbaseRequest.requirements[key].number = (index + 1).toString();
          onbaseRequest.requirements[key].refNumber = key;
          onbaseRequest.requirements[key].status =
            onbaseRequest.requirements[key].completedBy != undefined
              ? 'Completado'
              : 'Pendiente';
          return onbaseRequest.requirements[key];
        }
      )
    );
  }

  private transformDate(
    onbaseRequestResponse: ResponseModel<CreditRequest>
  ) {
    const onbaseRequest = onbaseRequestResponse.response;
    onbaseRequest.createdDate = this.transformCreatedDate(onbaseRequest);
    onbaseRequest.createdHour = this.transformCreatedHour(onbaseRequest);
    onbaseRequest.origin =
      onbaseRequest.origin === 'STORE' ? 'TIENDA' : 'WEB';
    onbaseRequest.parsedStatus =
      this.getOnbaseStatusMapValue().get(this.getStatus(onbaseRequest)) || '';
    onbaseRequest.isAval = `${this.getIsAvalRequest(onbaseRequest)}`;
    return onbaseRequestResponse;
  }

  private transformCreatedHour(creditRequest: CreditRequest): string {
    return (
      this.datePipe.transform(
        creditRequest.createdTimezoneDate,
        `hh:mm:ss a`
      ) || ``
    );
  }

  private transformCreatedDate(creditRequest: CreditRequest): string {
    return (
      this.datePipe.transform(
        creditRequest.createdTimezoneDate,
        `dd-MM-yyyy`
      ) || ``
    );
  }

  getOnbaseStatusMapValue(): Map<string, string> {
    return new Map<string, string>()
      .set('NO_STATUS', 'SIN ESTADO')
      .set('APPROVED', 'APROBADA')
      .set('REJECTED', 'RECHAZADA')
      .set('CONTROL_TABLE', 'MESA DE CONTROL')
      .set('TELEPHONE_INVESTIGATION', 'INVESTIGACIÓN TELEFÓNICA')
      .set('HOME_DOMESTIC_INVESTIGATION', 'INVESTIGACIÓN DOMICILIARIA CASA')
      .set('WORK_DOMESTIC_INVESTIGATION', 'INVESTIGACIÓN DOMICILIARIA TRABAJO')
      .set('HUMAN_RESOURCES', 'RECURSOS HUMANOS')
      .set('ANALISYS_AND_APPROVAL', 'ANÁLISIS Y APROBACIÓN')
      .set('REPROGRAMMING_IT', 'REPROGRAMACIÓN IT')
      .set('REPROGRAMMING_HOUSE_ID', 'REPROGRAMACIÓN CASA')
      .set('REPROGRAMMING_WORK_ID', 'REPROGRAMACIÓN TRABAJO')
      .set('WITH_REQUIREMENTS', 'EN REQUERIMIENTO')
      .set('', '');
  }

  getStatus(creditRequest: CreditRequest): string {
    return creditRequest.status[0] ? creditRequest.status[0] : ``;
  }

  getStatusClassByOnbaseStatus(status: string) {
    switch (status) {
      case CreditStatus.APPROVED:
      case CreditProcessStatus.PROCESSED:
        return 'badge-success';
      case CreditStatus.TELEPHONE_INVESTIGATION:
      case CreditStatus.CONTROL_TABLE:
      case CreditStatus.HOME_DOMESTIC_INVESTIGATION:
      case CreditStatus.WORK_DOMESTIC_INVESTIGATION:
      case CreditStatus.HUMAN_RESOURCES:
      case CreditStatus.ANALISYS_AND_APPROVAL:
      case CreditStatus.WITH_REQUIREMENTS:
      case CreditProcessStatus.DRAFT:
        return 'badge-warning';
      case CreditStatus.NO_STATUS:
      case CreditStatus.REPROGRAMMING_IT:
      case CreditStatus.REPROGRAMMING_HOUSE_ID:
      case CreditStatus.REPROGRAMMING_WORK_ID:
      case CreditProcessStatus.UNPROCESSED:
      case ``:
        return 'badge-secondary';
      case CreditStatus.REJECTED:
      case CreditProcessStatus.FAILED:
        return 'badge-danger';
      default:
        return ``;
    }
  }

  getParsedComments(): string {
    return this.creditRequest.comments.reduce(
      (acc, act) => `${acc} ${act}`,
      ``
    );
  }

  async handleTimerClick(): Promise<void> {
    //
  }

  getRequirementsTableHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable('number', 'Nº'),
      new ColumnTable('area', 'Area'),
      new ColumnTable('type', 'Tipo'),
      new ColumnTable('createdTimezoneDate', 'Fecha Creación', {
        type: 'date',
      }),
      new ColumnTable('attachment', 'Archivo Adjunto', { type: 'switch' }),
      new ColumnTable('completedBy', 'Confirmado Por'),
      new ColumnTable('status', 'Estado', { type: 'badge' }),
      new ColumnTable('', 'Opciones', {
        type: 'action',
        iconClass: 'fa fa-cogs',
        isIcon: true,
        actions: [
          {
            fn: async (requirement: CreditRequestRequirement) => {
              const dataToConfirmRequirement =
                await this.requirementDetailModalService.show(
                  requirement,
                  this.creditPlans.find(
                    (plan) => plan.code === this.creditRequest.procedure
                  ) || new OnbaseProcedureResponseModel(),
                  this.onbaseDocs,
                  this.creditRequest
                );

              if (requirement.attachment && dataToConfirmRequirement && dataToConfirmRequirement?.files.length <= 0) {
                this.exceptionSolutionService.showAlertWithSolution(`Solicitud de Crédito ${this.creditRequest.code}`, `Información incompleta </br> Es necesario agregar adjuntos para este requerimiento`);
              }
              else {
                const confirmRequirementResponse = await this.onbaseService
                  .confirmRequirement(
                    this.creditRequest.number,
                    dataToConfirmRequirement?.comment || '',
                    requirement.refNumber
                  )
                  .toPromise();

                if (confirmRequirementResponse) {
                  if (
                    dataToConfirmRequirement &&
                    dataToConfirmRequirement?.files.length > 0
                  ) {
                    for (const file of dataToConfirmRequirement?.files) {
                      const requestToImportFile: ImportRequirementDocumentRequest =
                      {
                        file: file.file.split(',').pop() || '',
                        extention: file.extentionFile,
                        identificacion:
                          this.creditRequest.customer.mainPersonalInfo.fiscalId,
                        numSolicitud: this.creditRequestNumber,
                        tipoArchivo: this.onbaseService.getFileType(
                          file.fileName
                        ).code,
                        tipoDocumental: file.documentType,
                      };

                      const importDocumentResponse = await this.onbaseService
                        .importDocumentForRequirement(requestToImportFile)
                        .toPromise();
                    }
                  }

                  this.creditRequest = (
                    await this.readCreditRequestAndTransformDate(
                      this.creditRequestNumber
                    )
                  ).response;

                  Swal.fire({
                    title: `Requerimientos de crédito`,
                    html: 'Operacion Exitosa </br> Se completo el requerimiento.',
                    icon: 'success',
                    confirmButtonText: 'Aceptar',
                  });
                }
              }
            },
            icon: 'fa fa-eye',
            tooltip: 'Ver Requerimiento',
            isDisabled: false,
          },
        ],
      }),
    ];
  }

  handleCompleteRequestClick(): void {
    this.router.navigate([`/creditRequests/modify`, this.creditRequest.code]);
  }

  handleInvoiceClick(): void {
    this.router.navigate([`sales`], {
      state: {
        customer: this.creditRequest.customer,
      },
    });
  }

  handlePreAnalisisClick(): void {
    this.router.navigate(['customer', this.creditRequest.customer.code]);
  }

  async handleDataChange(): Promise<void> {
    this.creditRequest = (
      await this.readCreditRequestAndTransformDate(this.creditRequestNumber)
    ).response;

    await this.setAndConfigureDataForTimers();
  }

  getProcessingStatusMapValue(status: string): string {
    const map = new Map<string, string>()
      .set('PROCESSED', 'PROCESADA')
      .set('FAILED', 'FALLO')
      .set('UNPROCESSED', 'PROCESANDO...')
      .set('DRAFT', 'EN BORRADORES');

    return map.get(status) || '';
  }

  private getIsAvalRequest(creditRequest: CreditRequest): boolean {
    return (
      creditRequest.requestData.Codigo_Solicitud_Principal != undefined &&
      creditRequest.requestData.Codigo_Solicitud_Principal !== ''
    );
  }

  handleGoToClick(goToSecundary: boolean) {
    this.router.navigate(
      [
        'creditRequests',
        goToSecundary
          ? this.creditRequest.requestData.codigoSolicitudAval
          : this.creditRequest.requestData.Codigo_Solicitud_Principal,
      ],
      { replaceUrl: true }
    );
  }
}
